import { Component } from '@theme/component';

/**
 * <versel-help-faq>: FAQ for the help center with one tab per topic and a search that looks
 * across every topic. Markup comes from sections/versel-help-faq.liquid and its
 * _versel-help-topic / _versel-help-question blocks. Without JavaScript the tabs and search stay
 * hidden, every topic is listed and each question remains a working <details> accordion.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [search] - Search row, hidden until this component runs.
 * @property {HTMLInputElement} [searchInput]
 * @property {HTMLButtonElement} [clearButton]
 * @property {HTMLElement} [status] - Polite live region for the result count.
 * @property {HTMLElement} [empty] - "No answers" message.
 * @property {HTMLElement} [emptyQuery] - Where the searched text is echoed in the empty message.
 *
 * @extends {Component<Refs>}
 */
class VerselHelpFaq extends Component {
  /** @type {string} Id of the topic tab that is showing. */
  #topic = '';
  #query = '';
  #statusTimer = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }

  #init() {
    if (this.refs.search) this.refs.search.hidden = false;
    this.#renderChips();
    // A link like #faq-returns-exchanges opens on that topic's tab.
    if (!this.showTopicByAnchor(window.location.hash.slice(1))) this.#apply();
  }

  /** @returns {HTMLElement[]} */
  get #topics() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-help-topic]')]);
  }

  /** One tab per topic; the first topic shows until another is picked. */
  #renderChips() {
    const container = this.querySelector('[data-help-chips]');
    if (!(container instanceof HTMLElement)) return;
    const topics = this.#topics;

    container.replaceChildren();
    for (const topic of topics) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'vh-chip';
      chip.dataset.topic = topic.dataset.helpTopic ?? '';
      chip.setAttribute('aria-pressed', 'false');
      chip.setAttribute('on:click', '/selectTopic');
      chip.textContent = topic.dataset.helpTopicTitle ?? '';
      container.append(chip);
    }
    container.hidden = topics.length < 2;
    if (!topics.some((topic) => topic.dataset.helpTopic === this.#topic)) this.#topic = topics[0]?.dataset.helpTopic ?? '';
  }

  /**
   * Shows the topic whose element id is `anchor` (e.g. "faq-returns-exchanges") and scrolls to it.
   * @param {string} anchor
   * @returns {boolean} Whether a topic matched.
   */
  showTopicByAnchor(anchor) {
    const topic = anchor ? this.#topics.find((item) => item.id === anchor) : undefined;
    if (!topic) return false;

    this.#topic = topic.dataset.helpTopic ?? '';
    this.#query = '';
    if (this.refs.searchInput) this.refs.searchInput.value = '';
    this.#apply();
    requestAnimationFrame(() => topic.scrollIntoView({ block: 'start' }));
    return true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#statusTimer);
  }

  /** @param {InputEvent} event */
  filter(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    this.#query = input.value.trim().toLowerCase();
    this.#apply({ announce: true });
  }

  clear() {
    const { searchInput } = this.refs;
    if (searchInput) {
      searchInput.value = '';
      searchInput.focus();
    }
    this.#query = '';
    this.#apply({ announce: true });
  }

  /** @param {MouseEvent} event */
  selectTopic(event) {
    const chip = event.target;
    if (!(chip instanceof HTMLElement)) return;
    this.#topic = chip.dataset.topic ?? '';
    this.#query = '';
    if (this.refs.searchInput) this.refs.searchInput.value = '';
    this.#apply({ announce: true });
  }

  /** @param {{ announce?: boolean }} [options] */
  #apply({ announce = false } = {}) {
    const query = this.#query;
    const words = query.split(/\s+/).filter(Boolean);
    let visibleCount = 0;

    // While searching, look across every topic and leave no tab selected.
    for (const chip of this.querySelectorAll('[data-help-chips] [data-topic]')) {
      if (chip instanceof HTMLElement) {
        chip.setAttribute('aria-pressed', String(!query && chip.dataset.topic === this.#topic));
      }
    }

    for (const topic of this.#topics) {
      const topicMatches = Boolean(query) || topic.dataset.helpTopic === this.#topic;
      let topicVisible = 0;

      for (const question of topic.querySelectorAll('[data-help-question]')) {
        if (!(question instanceof HTMLDetailsElement)) continue;
        const text = (question.textContent ?? '').toLowerCase();
        const matches = topicMatches && words.every((word) => text.includes(word));
        question.hidden = !matches;
        if (matches) topicVisible += 1;
      }

      topic.hidden = topicVisible === 0;
      visibleCount += topicVisible;
    }

    // With a search, open the answers when only a few match so the shopper sees them right away.
    if (query) {
      for (const question of this.querySelectorAll('[data-help-question]:not([hidden])')) {
        if (question instanceof HTMLDetailsElement) question.open = visibleCount <= 3;
      }
    }

    const { empty, emptyQuery, clearButton, status } = this.refs;
    if (clearButton) clearButton.hidden = !query;
    if (empty) empty.hidden = visibleCount > 0;
    if (emptyQuery) emptyQuery.textContent = query;

    if (announce && status) {
      clearTimeout(this.#statusTimer);
      this.#statusTimer = window.setTimeout(() => {
        const template = visibleCount === 1 ? status.dataset.one : status.dataset.other;
        status.textContent = visibleCount > 0 ? (template ?? '').replace('[count]', String(visibleCount)) : '';
      }, 400);
    }
  }
}

if (!customElements.get('versel-help-faq')) customElements.define('versel-help-faq', VerselHelpFaq);

/*
 * The contact form is a last resort: it stays collapsed ([data-help-gate]) and only opens when a
 * shopper follows a link to #contact on this page (a "Contact us" link in an answer, the FAQ's
 * "no answers" message, or a shortcut set to the contact form). A link with
 * data-help-contact-topic also preselects that topic. Links to #faq-… switch the FAQ to that tab.
 */
if (!(/** @type {any} */ (window).__verselHelpCenter)) {
  /** @type {any} */ (window).__verselHelpCenter = true;

  /** @param {string} [topic] */
  const openContact = (topic) => {
    const gate = document.querySelector('[data-help-gate]');
    if (gate instanceof HTMLDetailsElement) gate.open = true;

    const select = document.querySelector('[data-help-topic-select]');
    if (topic && select instanceof HTMLSelectElement) {
      const option = [...select.options].find((item) => item.value === topic);
      if (option) select.value = option.value;
    }

    // Let the anchor scroll happen, then put the cursor in the message box.
    window.setTimeout(() => {
      const message = document.querySelector('[data-help-gate] textarea');
      if (message instanceof HTMLTextAreaElement) message.focus({ preventScroll: true });
    }, 450);
  };

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href*="#"]') : null;
    if (!(link instanceof HTMLAnchorElement) || link.pathname !== window.location.pathname) return;

    if (link.hash === '#contact') {
      openContact(link.dataset.helpContactTopic);
      return;
    }

    const faq = document.querySelector('versel-help-faq');
    if (link.hash.startsWith('#faq-') && faq instanceof VerselHelpFaq && faq.showTopicByAnchor(link.hash.slice(1))) {
      event.preventDefault();
      history.replaceState(null, '', link.hash);
    }
  });

  if (window.location.hash === '#contact') openContact();
}
